import React, { useState } from 'react';
import { ChevronDown, HelpCircle, Database, Lock, RefreshCw, TrendingUp, Calendar, Settings } from 'lucide-react';

interface FAQItem {
  id: string;
  category: 'データ管理' | 'SBT発行' | 'セキュリティ' | '確認・レポート';
  question: string;
  answer: React.ReactNode;
  icon: React.ReactNode;
}

const FAQ_ITEMS: FAQItem[] = [
  {
    id: 'data-storage',
    category: 'データ管理',
    question: 'SBTテンプレートと発行データはどこに保存されていますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>📍 保存場所：</strong> あなたのパソコン/タブレット/スマートフォンのローカルストレージ</p>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3">
          <p className="font-semibold text-blue-900">📍 保存場所：</p>
          <ul className="space-y-1 text-blue-800 text-xs">
            <li>• <strong>IndexedDB</strong>：テンプレート・SBT発行データ（主要）</li>
            <li>• <strong>localStorage</strong>：バックアップ・設定情報</li>
            <li>• 容量：50MB～数GB（デバイス依存）</li>
          </ul>
        </div>
        <p className="text-gray-600"><strong>✅ メリット：</strong> サーバーなし、ユーザー管理不要、完全にあなたの管理下</p>
        <p className="text-gray-600"><strong>⚠️ 注意：</strong> アプリ削除時にデータも削除されます（エクスポート推奨）</p>
      </div>
    ),
    icon: <Database className="w-5 h-5" />,
  },
  {
    id: 'data-persistence',
    category: 'データ管理',
    question: 'インターネット接続なしでデータは保存されますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>✅ はい、完全に保存されます</strong></p>
        <div className="bg-green-50 border border-green-200 rounded p-2 sm:p-3 space-y-2">
          <p className="font-semibold text-green-900 text-sm">🟢 オフライン動作対応：</p>
          <ul className="text-green-800 space-y-1 text-xs">
            <li>• テンプレート作成 ✓</li>
            <li>• SBT データ準備・管理 ✓</li>
            <li>• ローカルデータ表示・検索 ✓</li>
          </ul>
        </div>
        <div className="bg-red-50 border border-red-200 rounded p-2 sm:p-3 space-y-2">
          <p className="font-semibold text-red-900 text-sm">❌ オフラインでは不可（インターネット接続必須）：</p>
          <ul className="text-red-800 space-y-1 text-xs">
            <li>• <strong>QR コード生成</strong> - MetaMask等ウォレット接続が必要</li>
            <li>• <strong>SBT実際の発行</strong> - Pinata (IPFS) へのメタデータアップロード</li>
            <li>• <strong>JPYC 決済処理</strong> - リアルタイムトランザクション処理・確認</li>
            <li>• <strong>ブロックチェーン記録</strong> - Polygon等ネットワークへのトランザクション送信</li>
            <li>• <strong>SBT画像保存</strong> - 画像ファイルのIPFS分散保存</li>
            <li>• 新版アプリ更新（接続時に自動更新）</li>
          </ul>
        </div>
      </div>
    ),
    icon: <RefreshCw className="w-5 h-5" />,
  },
  {
    id: 'multi-device',
    category: 'データ管理',
    question: '複数のパソコン・タブレットで同じデータを使いたい',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>📌 データは各デバイスで独立しています</strong></p>
        <p className="text-gray-600">（サーバーがないため、自動同期はできません）</p>
        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 space-y-2">
          <p className="font-semibold text-yellow-900">💡 対策方法：</p>
          <ol className="text-yellow-800 space-y-2">
            <li><strong>1. エクスポート</strong>
              <p className="text-xs">パソコンA → 設定 → データエクスポート → JSON ファイル保存</p>
            </li>
            <li><strong>2. インポート</strong>
              <p className="text-xs">パソコンB → 設定 → データインポート → ファイル選択</p>
            </li>
            <li><strong>3. USB/クラウド共有</strong>
              <p className="text-xs">USB メモリまたは Google Drive で JSON ファイルを共有</p>
            </li>
          </ol>
        </div>
      </div>
    ),
    icon: <TrendingUp className="w-5 h-5" />,
  },
  {
    id: 'sbt-issuance',
    category: 'SBT発行',
    question: 'SBT発行の３つのパターンの違いは？',
    answer: (
      <div className="space-y-3 text-sm">
        <div className="space-y-3">
          <div className="border-l-4 border-purple-500 pl-3 py-2">
            <p className="font-semibold text-purple-700">1️⃣ 毎回発行（per_payment）</p>
            <p className="text-gray-600">支払いのたびに SBT を 1 つずつ発行します</p>
            <p className="text-xs text-gray-500">例：コーヒー購入 → SBT1個発行、また購入 → SBT1個追加発行</p>
            <p className="text-xs text-amber-600 font-medium">⚠️ SBT発行・JPYC QR決済にはインターネット接続が必要です</p>
          </div>
          <div className="border-l-4 border-blue-500 pl-3 py-2">
            <p className="font-semibold text-blue-700">2️⃣ N回後発行（after_count）</p>
            <p className="text-gray-600">決められた回数の支払いに達したら SBT を 1 つ発行</p>
            <p className="text-xs text-gray-500">例：10回購入したらスタンプカード進呈 → SBT1個発行</p>
          </div>
          <div className="border-l-4 border-green-500 pl-3 py-2">
            <p className="font-semibold text-green-700">3️⃣ 期間内発行（time_period）</p>
            <p className="text-gray-600">指定期間内に支払いがあれば SBT を発行</p>
            <p className="text-xs text-gray-500">例：10月1日～31日 キャンペーン期間内の購入 → SBT発行</p>
          </div>
        </div>
      </div>
    ),
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    id: 'sbt-status',
    category: 'SBT発行',
    question: 'SBTの「有効」と「特典獲得」の状態の違いは？',
    answer: (
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-green-50 border border-green-200 rounded p-2 sm:p-3">
            <p className="font-semibold text-green-900 text-sm">🟢 有効（Active）</p>
            <p className="text-green-800 text-xs mt-1">スタンプを集め中の状態</p>
            <p className="text-green-700 text-xs mt-2">例：スタンプ 3/10 進行中</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3">
            <p className="font-semibold text-blue-900 text-sm">🔵 特典獲得（Redeemed）</p>
            <p className="text-blue-800 text-xs mt-1">スタンプが満杯になった状態</p>
            <p className="text-blue-700 text-xs mt-2">例：スタンプ 10/10 完了 → 特典獲得</p>
          </div>
        </div>
      </div>
    ),
    icon: <TrendingUp className="w-5 h-5" />,
  },
  {
    id: 'template-period',
    category: 'SBT発行',
    question: '期間指定テンプレートの設定方法は？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>🗓️ 開始日と終了日を指定するテンプレート</strong></p>
        <div className="bg-purple-50 border border-purple-200 rounded p-3 space-y-2">
          <p className="font-semibold text-purple-900">設定方法：</p>
          <ol className="text-purple-800 space-y-1">
            <li>1. テンプレート作成 → 「発行パターン」で「期間指定」を選択</li>
            <li>2. 「開始日」カレンダーで最初の日を指定</li>
            <li>3. 「終了日」カレンダーで最後の日を指定</li>
            <li>4. この期間内の支払いで SBT が自動発行</li>
          </ol>
        </div>
        <p className="text-gray-600"><strong>例：</strong> 2025年11月14日～2025年12月31日のキャンペーン</p>
      </div>
    ),
    icon: <Calendar className="w-5 h-5" />,
  },
  {
    id: 'sbt-network-requirement',
    category: 'SBT発行',
    question: 'SBTの発行には本当にインターネット接続が必要ですか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>⚠️ はい、SBT発行・JPYC QR決済にはインターネット接続が必要です</strong></p>
        <div className="bg-amber-50 border border-amber-300 rounded p-3 space-y-2">
          <p className="font-semibold text-amber-900">🌐 必要な接続：</p>
          <ol className="text-amber-800 space-y-1">
            <li><strong>1. Pinata (IPFS) サービス</strong>
              <p className="text-xs">SBTメタデータ（名前・説明・画像）の分散保存</p>
              <p className="text-xs font-mono">api.pinata.cloud</p>
            </li>
            <li><strong>2. ブロックチェーンネットワーク</strong>
              <p className="text-xs">実際のSBT発行・所有権登録</p>
              <p className="text-xs font-mono">Polygon: polygon-rpc.com</p>
            </li>
          </ol>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
          <p className="font-semibold text-blue-900 text-sm">💡 作業の分離：</p>
          <ul className="text-blue-800 space-y-1 text-xs">
            <li>• <strong>オフライン</strong>：テンプレート作成、データ準備</li>
            <li>• <strong>オンライン</strong>：実際のSBT発行（IPFS + ブロックチェーン）</li>
          </ul>
        </div>
        <p className="text-gray-600"><strong>📱 推奨環境：</strong> 安定したWiFi接続またはモバイルデータ通信</p>
      </div>
    ),
    icon: <RefreshCw className="w-5 h-5" />,
  },
  {
    id: 'sales-history',
    category: 'データ管理',
    question: '売上・決済履歴はどのように記録されますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>💳 決済履歴の記録方法</strong></p>
        <div className="bg-indigo-50 border border-indigo-200 rounded p-3 space-y-2">
          <p className="font-semibold text-indigo-900">📊 記録される情報：</p>
          <ul className="text-indigo-800 space-y-1">
            <li>• 決済額（JPYC）</li>
            <li>• 決済日時</li>
            <li>• 顧客ウォレットアドレス（接続している場合）</li>
            <li>• SBT 発行状況（発行済み/未発行）</li>
            <li>• トランザクションハッシュ（ブロックチェーン記録）</li>
          </ul>
        </div>
        <p className="text-gray-600"><strong>📍 保存場所：</strong> IndexedDB（デバイスローカル）</p>
        <p className="text-gray-600"><strong>📤 エクスポート方法：</strong> 設定 → データエクスポート → CSV/JSON 形式</p>
      </div>
    ),
    icon: <Database className="w-5 h-5" />,
  },
  {
    id: 'security',
    category: 'セキュリティ',
    question: 'データは安全に保護されていますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>🔐 セキュリティ対策</strong></p>
        <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
          <p className="font-semibold text-green-900">✅ 実装済み対策：</p>
          <ul className="text-green-800 space-y-1">
            <li>• ローカルストレージ（サーバー保存なし）</li>
            <li>• IndexedDB 自動暗号化（OS 依存）</li>
            <li>• Service Worker による安全なキャッシング</li>
            <li>• HTTPS 推奨（デプロイ時）</li>
          </ul>
        </div>
        <p className="text-yellow-600 text-xs"><strong>⚠️ 注意：</strong> 秘密鍵管理は MetaMask に委譲されます</p>
      </div>
    ),
    icon: <Lock className="w-5 h-5" />,
  },
  {
    id: 'confirm-issue',
    category: '確認・レポート',
    question: 'SBT の発行状況をどのように確認・管理しますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>📊 SBT 発行状況の確認方法</strong></p>
        <div className="space-y-2">
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="font-semibold text-blue-900">1️⃣ 配布先ウォレット統計</p>
            <p className="text-blue-800 text-xs">ウォレットアドレス単位で SBT 発行数を集計</p>
            <ul className="text-blue-700 text-xs mt-1">
              <li>• 総 SBT 数</li>
              <li>• 有効な SBT 数</li>
              <li>• 特典獲得済み SBT 数</li>
              <li>• スタンプ進捗率</li>
            </ul>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded p-3">
            <p className="font-semibold text-purple-900">2️⃣ SBT 詳細リスト</p>
            <p className="text-purple-800 text-xs">個別の SBT を確認・管理</p>
            <ul className="text-purple-700 text-xs mt-1">
              <li>• PC 版：テーブルビューで全体俯瞰</li>
              <li>• スマホ版：ウォレット別カードビュー</li>
              <li>• 各 SBT の詳細情報確認可能</li>
            </ul>
          </div>
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="font-semibold text-green-900">3️⃣ テンプレート別追跡</p>
            <p className="text-green-800 text-xs">各テンプレートで何個の SBT が発行されたか確認</p>
            <ul className="text-green-700 text-xs mt-1">
              <li>• テンプレート発行数</li>
              <li>• 各パターンの発行状況</li>
            </ul>
          </div>
        </div>
      </div>
    ),
    icon: <TrendingUp className="w-5 h-5" />,
  },
  {
    id: 'distribution-status',
    category: '確認・レポート',
    question: 'SBT の配布状況をどのように確認しますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>📍 配布状況の確認ポイント</strong></p>
        <div className="space-y-2">
          <div className="border-l-4 border-purple-500 pl-3 py-2">
            <p className="font-semibold">📊 配布先ウォレット統計（SBT管理画面トップ）</p>
            <p className="text-gray-600 text-xs">各ウォレットアドレスに配布された SBT の集計</p>
            <div className="bg-gray-50 p-2 rounded mt-1 text-xs text-gray-700">
              <p>• <strong>ウォレット</strong>：配布先のアドレス</p>
              <p>• <strong>総 SBT：</strong> 配布した SBT 総数</p>
              <p>• <strong>有効：</strong> 進行中の SBT 数</p>
              <p>• <strong>特典獲得済み：</strong> 完了した SBT 数</p>
              <p>• <strong>進捗：</strong> スタンプ集計と完了率</p>
            </div>
          </div>
          <div className="border-l-4 border-blue-500 pl-3 py-2">
            <p className="font-semibold">📋 SBT 詳細表示</p>
            <p className="text-gray-600 text-xs">統計の下に個別 SBT を表示</p>
            <ul className="text-xs text-gray-700 mt-1">
              <li>• PC：テーブル形式（ウォレット、SBT名、スタンプ、発行日、状態）</li>
              <li>• タブレット：カードビュー（読みやすさ重視）</li>
            </ul>
          </div>
          <div className="border-l-4 border-green-500 pl-3 py-2">
            <p className="font-semibold">🔍 情報確認方法</p>
            <p className="text-gray-600 text-xs">SBT 管理画面で以下の確認が可能</p>
            <ul className="text-xs text-gray-700 mt-1">
              <li>• ウォレット別の SBT 集計</li>
              <li>• 各ウォレットの進捗状況</li>
              <li>• テンプレート別の発行数</li>
              <li>• 日付順の発行履歴</li>
            </ul>
          </div>
        </div>
      </div>
    ),
    icon: <Settings className="w-5 h-5" />,
  },
  {
    id: 'jpyc-pay-integration',
    category: 'SBT発行',
    question: 'JPYCペイアプリとはどう連携しますか？',
    answer: (
      <div className="space-y-3 text-sm">
        <p><strong>🔗 JPYCペイアプリとの連携について</strong></p>
        <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
          <p className="font-semibold text-blue-900">📱 JPYCペイアプリ（<a href="https://jpyc-pay.app/" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">https://jpyc-pay.app/</a>）との関係：</p>
          <ul className="text-blue-800 space-y-2">
            <li><strong>• 店舗側：</strong> この管理アプリでQRコード生成・SBT発行設定</li>
            <li><strong>• 顧客側：</strong> JPYCペイアプリでQR読み取り・支払い・SBT受け取り</li>
            <li><strong>• 連携機能：</strong> 店舗情報・SBTテンプレート・決済データの統合管理</li>
          </ul>
        </div>
        <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
          <p className="font-semibold text-green-900">✅ 対応機能：</p>
          <ul className="text-green-800 space-y-1">
            <li>• QRコード決済連携（リアルタイム処理）</li>
            <li>• SBT自動発行（支払い完了時）</li>
            <li>• 店舗情報同期（名前・説明・画像等）</li>
            <li>• 決済履歴・SBT発行状況の管理</li>
          </ul>
        </div>
        <p className="text-gray-600"><strong>📍 利用フロー：</strong></p>
        <ol className="text-gray-700 space-y-1 ml-4">
          <li>1. この管理アプリで店舗設定・SBTテンプレート作成</li>
          <li>2. QRコード生成・表示</li>
          <li>3. 顧客がJPYCペイアプリでQR読み取り</li>
          <li>4. JPYC決済完了・SBT自動発行</li>
          <li>5. この管理アプリで決済確認・SBT発行状況確認</li>
        </ol>
      </div>
    ),
    icon: <TrendingUp className="w-5 h-5" />,
  },
];

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  if (!isOpen) return null;

  const categories = Array.from(new Set(FAQ_ITEMS.map(item => item.category)));
  const filteredItems = selectedCategory 
    ? FAQ_ITEMS.filter(item => item.category === selectedCategory)
    : FAQ_ITEMS;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col mx-2 sm:mx-0">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            <h2 className="text-white text-lg sm:text-xl font-bold">よくある質問（FAQ）</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-800 rounded-lg p-1 sm:p-2 transition text-lg sm:text-base"
          >
            ✕
          </button>
        </div>

        {/* Category Filter */}
        <div className="border-b px-3 sm:px-6 py-2 sm:py-3 bg-gray-50">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 sm:-mx-6 px-3 sm:px-6 scrollbar-thin scrollbar-thumb-gray-300">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full whitespace-nowrap font-semibold text-xs sm:text-sm transition flex-shrink-0 ${
                selectedCategory === null
                  ? 'bg-purple-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-400'
              }`}
            >
              すべて
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full whitespace-nowrap font-semibold text-xs sm:text-sm transition flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:border-purple-400'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-3 sm:px-6 py-2 sm:py-4 space-y-2">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg overflow-hidden hover:border-purple-400 transition"
            >
              <button
                onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                className="w-full px-3 sm:px-4 py-3 flex items-start gap-2 sm:gap-3 hover:bg-gray-50 transition text-left"
              >
                <div className="text-purple-600 mt-1 flex-shrink-0">{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm sm:text-base break-words">{item.question}</p>
                  <p className="text-xs text-gray-500 mt-1">{item.category}</p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0 transition ${
                    expandedId === item.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedId === item.id && (
                <div className="px-3 sm:px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm">
                  {item.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-3 sm:px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold transition text-sm sm:text-base"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
